import {
  ActionIcon, Alert, Badge, Button, Group, Loader, NumberInput, Select, Slider, Stack, Switch, Table,
  Text, Tooltip,
} from "@mantine/core";
import {
  IconCalendarSearch, IconChartLine, IconClick, IconEye, IconEyeOff, IconInfoCircle, IconSatellite,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import ImageLayer from "ol/layer/Image";
import VectorLayer from "ol/layer/Vector";
import type OlMap from "ol/Map";
import { toLonLat } from "ol/proj";
import ImageStatic from "ol/source/ImageStatic";
import VectorSource from "ol/source/Vector";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";

import { authFetch, errorMessage } from "../api/client";
import { http } from "../api/http";
import { useT } from "../i18n/script";
import { DockCard } from "./MapDock";

interface Visual { code: string; name: string; description?: string; legend: { color: string; label: string }[] }
interface Index { code: string; name: string; description?: string; ramp: [number, string][]; classes: { od: number; do: number; naziv: string }[] }
interface Layers { visual: Visual[]; indices: Index[] }
interface Scene { date: string; cloud: number | null }
interface PointRes {
  found: boolean; message?: string; lon: number; lat: number; date?: string; scl?: number; scl_naziv?: string; pouzdano?: boolean;
  bands?: Record<string, number>; indices?: { code: string; name: string; value: number | null; tumacenje: string | null }[];
  snimaka_u_periodu?: number; bez_oblaka?: number;
}

const today = () => new Date().toISOString().slice(0, 10);
const fmtD = (d: string) => d.split("-").reverse().join(".") + ".";
const SHORT: Record<string, string> = { TRUE_COLOR: "Prirodne boje", FALSE_COLOR: "Lažne boje", AGRICULTURE: "Poljoprivreda" };
const MAIN = ["NDVI", "NDRE", "NDMI", "GNDVI", "EVI", "BSI"];
const BAND_NAME: Record<string, string> = { B02: "plavi", B03: "zeleni", B04: "crveni", B05: "red edge", B08: "NIR", B11: "SWIR 1", B12: "SWIR 2" };

/**
 * Sentinel-2 na karti, za vidljivi dio karte: prirodne boje (vidljivi spektar), lažne boje, kompozit za poljoprivredu
 * ili indeks. Uz prikaz: legenda (šta znače boje / skala i klase indeksa), datum snimka i očitavanje vrijednosti klikom.
 * Slika i očitavanje se računaju na zahtjev, da se ne troši kvota.
 */
export function useSentinel(mapRef: RefObject<OlMap | null>, open: boolean, onClose: () => void, enabled = true):
  { controls: ReactNode; legend: ReactNode; result: ReactNode } {
  const { t } = useT();
  const layers = useQuery({ queryKey: ["s2-view-layers"], staleTime: 600_000, enabled, queryFn: () => http.get<Layers>("/api/v1/sentinel/view/layers") });
  const status = useQuery({ queryKey: ["s2-status"], staleTime: 60_000, enabled,
    queryFn: () => http.get<{ configured: boolean; enabled: boolean; month_requests: number; month_limit: number; month_pu?: number; pu_limit?: number }>("/api/v1/sentinel/status") });
  const [layer, setLayer] = useState("TRUE_COLOR");
  const [day, setDay] = useState(today());
  const [back, setBack] = useState(10);
  const [opacity, setOpacity] = useState(0.9);
  const [scenes, setScenes] = useState<Scene[] | null>(null);
  const [busy, setBusy] = useState<"img" | "scn" | "st" | "pt" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [shown, setShown] = useState<{ layer: string; day: string; back: number } | null>(null);
  const [visible, setVisible] = useState(true);
  const [stat, setStat] = useState<string | null>(null);
  const [probe, setProbe] = useState(false);
  const [pt, setPt] = useState<PointRes | null>(null);
  const lyr = useRef<ImageLayer<ImageStatic> | null>(null);
  const mark = useRef(new VectorSource());
  const url = useRef<string | null>(null);

  // sloj sa oznakom očitane tačke
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !enabled) return;
    const v = new VectorLayer({ source: mark.current, zIndex: 960, style: new Style({ image: new CircleStyle({ radius: 7,
      fill: new Fill({ color: "rgba(255,255,255,0.25)" }), stroke: new Stroke({ color: "#ffd43b", width: 3 }) }) }) });
    map.addLayer(v);
    return () => {
      map.removeLayer(v);
      if (lyr.current) map.removeLayer(lyr.current);
      if (url.current) URL.revokeObjectURL(url.current);
      map.set("s2probe", false);
    };
  }, [mapRef, enabled]);
  useEffect(() => { lyr.current?.setOpacity(opacity); }, [opacity]);

  // očitavanje klikom: dok je uključeno, klik na kartu ne otvara voćnjak/parcelu
  const probeArgs = useRef({ day, back });
  probeArgs.current = { day: shown?.day ?? day, back: shown?.back ?? back };
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.set("s2probe", probe);
    const el = map.getTargetElement();
    if (el) el.style.cursor = probe ? "crosshair" : "";
    if (!probe) return;
    const key = map.on("singleclick", async (e) => {
      const [lon, lat] = toLonLat(e.coordinate);
      mark.current.clear();
      mark.current.addFeature(new Feature(new Point(e.coordinate)));
      setBusy("pt"); setErr(null);
      try {
        setPt(await http.get<PointRes>("/api/v1/sentinel/view/point",
          { lon: lon.toFixed(6), lat: lat.toFixed(6), date: probeArgs.current.day, days_back: Math.max(probeArgs.current.back, 10) }));
        status.refetch();
      } catch (ex) { setErr((ex as Error).message); } finally { setBusy(null); }
    });
    return () => { map.un("singleclick", key.listener); if (el) el.style.cursor = ""; };
  }, [probe, mapRef, status]);

  const view = () => {
    const map = mapRef.current;
    if (!map) return null;
    const size = map.getSize() ?? [1024, 768];
    const ext = map.getView().calculateExtent(size);
    const k = Math.min(1, 2048 / Math.max(size[0], size[1]));
    return { ext, bbox: ext.map((v) => v.toFixed(1)).join(","), w: Math.max(64, Math.round(size[0] * k)), h: Math.max(64, Math.round(size[1] * k)) };
  };

  async function show() {
    const v = view();
    if (!v) return;
    setBusy("img"); setErr(null);
    try {
      const q = new URLSearchParams({ bbox: v.bbox, layer, date: day, days_back: String(back), width: String(v.w), height: String(v.h) });
      const r = await authFetch(`/api/v1/sentinel/view/image?${q}`);
      if (!r.ok) {
        const body = (r.headers.get("content-type") ?? "").includes("json") ? await r.json() : await r.text();
        throw new Error(typeof body === "string" ? body : errorMessage(body));
      }
      const blobUrl = URL.createObjectURL(await r.blob());
      if (!lyr.current) {
        lyr.current = new ImageLayer({ zIndex: 450, opacity, properties: { code: "sentinel2", title: "Sentinel-2" } });
        mapRef.current?.addLayer(lyr.current);
      }
      lyr.current.setSource(new ImageStatic({ url: blobUrl, imageExtent: v.ext, projection: "EPSG:3857" }));
      lyr.current.setVisible(true);
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = blobUrl;
      setShown({ layer, day, back }); setVisible(true);
      status.refetch();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  async function findScenes() {
    const v = view();
    if (!v) return;
    setBusy("scn"); setErr(null);
    try {
      const s = await http.get<Scene[]>("/api/v1/sentinel/view/scenes", { bbox: v.bbox, max_cloud: 60 });
      setScenes(s);
      if (s[0]) { setDay(s[0].date); setBack(0); }
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  async function stats() {
    const v = view();
    if (!v || !idx) return;
    setBusy("st"); setErr(null);
    try {
      const r = await http.post<{ series?: Record<string, { date: string; mean: number | null }[]> }>(
        `/api/v1/sentinel/view/stats?bbox=${v.bbox}&layer=${layer}&date_to=${day}`);
      const rows = r.series?.[layer] ?? [];
      const last = [...rows].reverse().find((x) => x.mean != null);
      setStat(last ? `${t("Prosjek vidljivog dijela")}: ${last.mean!.toFixed(3)} (${fmtD(last.date)})${cls(idx, last.mean!) ? ` — ${t(cls(idx, last.mean!)!)}` : ""}`
        : t("Nema vrijednosti bez oblaka u zadnjih 60 dana"));
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  const toggleVisible = () => { const nv = !visible; lyr.current?.setVisible(nv); setVisible(nv); };
  const idx = layers.data?.indices.find((i) => i.code === layer);
  const shownVis = layers.data?.visual.find((v) => v.code === shown?.layer);
  const shownIdx = layers.data?.indices.find((i) => i.code === shown?.layer);
  const st = status.data;

  const controls = open && enabled ? (
        <DockCard id="s2-controls" title="Sentinel-2" icon={<IconSatellite size={16} />} onClose={onClose}
          badge={shown ? <Badge size="xs" variant="light" color="lime" style={{ flexShrink: 0 }}>{t(SHORT[shown.layer] ?? shown.layer)} · {fmtD(shown.day).slice(0, 6)}</Badge> : undefined}>
          <div style={{ paddingTop: 6 }}>
            {st && !st.configured ? (
              <Text size="xs" c="dimmed">{t("Satelitski izvor nije podešen (Administracija → Podešavanja → Satelitski podaci).")}</Text>
            ) : (
              <Stack gap={6}>
                <Select size="xs" label={t("Prikaz")} value={layer} onChange={(v) => v && setLayer(v)} allowDeselect={false}
                  data={[{ group: t("Boje (spektar)"), items: (layers.data?.visual ?? []).map((x) => ({ value: x.code, label: t(x.name) })) },
                    { group: t("Indeksi"), items: (layers.data?.indices ?? []).map((x) => ({ value: x.code, label: `${x.code} — ${x.name}` })) }]} />
                <Text size="xs" c="dimmed">{t(idx?.description ?? layers.data?.visual.find((v) => v.code === layer)?.description ?? "")}</Text>
                <Group gap={6} grow align="flex-end">
                  <Select size="xs" label={t("Datum snimka")} value={day} searchable allowDeselect={false} onChange={(v) => v && setDay(v)}
                    data={Array.from(new Set([day, ...(scenes ?? []).map((s) => s.date)])).map((d) => {
                      const s = scenes?.find((x) => x.date === d);
                      return { value: d, label: s ? `${fmtD(d)} · ${s.cloud == null ? "?" : Math.round(s.cloud)}% ${t("oblaka")}` : fmtD(d) };
                    })} />
                  <Tooltip label={t("Spaja najmanje oblačne snimke u zadatom broju dana do datuma; 0 = samo taj dan")} multiline w={240}>
                    <NumberInput size="xs" label={t("Mozaik (dana)")} min={0} max={60} value={back} onChange={(v) => setBack(Number(v) || 0)} />
                  </Tooltip>
                </Group>
                <Button size="compact-xs" variant="subtle" leftSection={<IconCalendarSearch size={13} />} loading={busy === "scn"} onClick={findScenes}>
                  {t("Dostupni snimci za vidljivi dio (90 dana)")}</Button>
                <Button size="xs" leftSection={<IconSatellite size={14} />} loading={busy === "img"} onClick={show}>
                  {t("Prikaži za vidljivi dio karte")}</Button>
                {shown && (
                  <Group gap={6} wrap="nowrap">
                    <Tooltip label={t(visible ? "Sakrij snimak" : "Prikaži snimak")}>
                      <ActionIcon size="sm" variant="light" onClick={toggleVisible}>{visible ? <IconEye size={14} /> : <IconEyeOff size={14} />}</ActionIcon>
                    </Tooltip>
                    <Slider size="xs" style={{ flex: 1 }} min={0} max={1} step={0.05} value={opacity} onChange={setOpacity} label={(v) => `${Math.round(v * 100)}%`} />
                  </Group>
                )}
                <Switch size="sm" mt={4} checked={probe} onChange={(e) => setProbe(e.currentTarget.checked)}
                  label={<Group gap={4}><IconClick size={14} />{t("Očitaj vrijednosti klikom na kartu")}</Group>}
                  description={t("Kanali, klasa površine i svi indeksi za tačku (10 × 10 m), sa tumačenjem")} />
                {idx && (
                  <Button size="compact-xs" variant="subtle" leftSection={<IconChartLine size={13} />} loading={busy === "st"} onClick={stats}>
                    {t("Prosjek indeksa za vidljivi dio")}</Button>
                )}
                {stat && <Text size="xs">{stat}</Text>}
                {err && <Alert color="red" p={6} fz="xs">{err}</Alert>}
                {st && <Text size="xs" c="dimmed">{t("Ovog mjeseca")}: {st.month_requests} / {st.month_limit || "∞"} {t("upita")} · {Math.round((st.month_pu ?? 0) * 10) / 10} / {st.pu_limit || "∞"} PU · {t("isti prikaz drugi put ide iz keša")}</Text>}
              </Stack>
            )}
          </div>
        </DockCard>
  ) : null;

  const legend = enabled && shown && visible && (shownVis || shownIdx) ? (
        <DockCard id="s2-legend" label={t("Legenda snimka")} title={shownIdx ? `${shownIdx.code} — ${shownIdx.name}` : t(shownVis!.name)}
          badge={<Tooltip label={t(shownIdx?.description ?? shownVis?.description ?? "")} multiline w={280}><IconInfoCircle size={14} opacity={0.6} style={{ flexShrink: 0 }} /></Tooltip>}>
          <Text size="10px" c="dimmed" mb={6} mt={4}>
            Sentinel-2 L2A · {shown.back ? `${t("mozaik")} ${fmtD(shiftDate(shown.day, -shown.back))} – ${fmtD(shown.day)}` : fmtD(shown.day)} · 10 m
          </Text>
          {shownVis && (
            <Stack gap={3}>
              {shownVis.legend.map((l) => (
                <Group key={l.label} gap={6} wrap="nowrap" align="flex-start">
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: l.color, flexShrink: 0, marginTop: 1, border: "1px solid rgba(0,0,0,.2)" }} />
                  <Text size="xs" lh={1.25}>{t(l.label)}</Text>
                </Group>
              ))}
            </Stack>
          )}
          {shownIdx && <IndexLegend idx={shownIdx} />}
          <Text size="10px" c="dimmed" mt={6}>{t(probe ? "Kliknite na kartu za očitavanje vrijednosti." : "Uključite „Očitaj vrijednosti klikom“ za vrijednost na tački.")}</Text>
        </DockCard>
  ) : null;

  const result = enabled && (pt || busy === "pt") && probe ? (
        <DockCard id="s2-point" label="Sentinel-2" title={t("Očitavanje tačke")} icon={<IconClick size={16} />}
          onClose={() => { setPt(null); mark.current.clear(); }}>
          <div style={{ paddingTop: 6 }}>
          {busy === "pt" ? <Group gap={6}><Loader size="xs" /><Text size="xs">{t("Očitavanje…")}</Text></Group> : pt && !pt.found ? (
            <Text size="xs" c="dimmed">{t(pt.message ?? "Nema snimka")}</Text>
          ) : pt && (
            <Stack gap={6}>
              <Group gap={6}>
                <Badge size="sm" variant="light">{fmtD(pt.date!)}</Badge>
                <Badge size="sm" color={pt.pouzdano ? "green" : "orange"} variant="light">{t(pt.scl_naziv ?? "")}</Badge>
              </Group>
              {!pt.pouzdano && <Text size="xs" c="orange">{t("U periodu nema snimka bez oblaka za ovu tačku — vrijednosti nisu pouzdane.")}</Text>}
              <Table fz="xs" verticalSpacing={2} withRowBorders={false}>
                <Table.Tbody>
                  {(pt.indices ?? []).filter((i) => MAIN.includes(i.code)).sort((a, b) => MAIN.indexOf(a.code) - MAIN.indexOf(b.code)).map((i) => (
                    <Table.Tr key={i.code}>
                      <Table.Td fw={700} w={60}>{i.code}</Table.Td>
                      <Table.Td w={56} ta="right" ff="monospace">{i.value == null ? "—" : i.value.toFixed(2)}</Table.Td>
                      <Table.Td c="dimmed">{t(i.tumacenje ?? "")}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <details>
                <summary style={{ cursor: "pointer", fontSize: 12 }}>{t("Ostali indeksi i reflektansa kanala")}</summary>
                <Table fz="xs" verticalSpacing={1} withRowBorders={false} mt={4}>
                  <Table.Tbody>
                    {(pt.indices ?? []).filter((i) => !MAIN.includes(i.code)).map((i) => (
                      <Table.Tr key={i.code}><Table.Td>{i.code}</Table.Td><Table.Td ta="right" ff="monospace">{i.value == null ? "—" : i.value.toFixed(3)}</Table.Td><Table.Td c="dimmed">{t(i.tumacenje ?? "")}</Table.Td></Table.Tr>
                    ))}
                    {Object.entries(pt.bands ?? {}).map(([k, v]) => (
                      <Table.Tr key={k}><Table.Td>{k}</Table.Td><Table.Td ta="right" ff="monospace">{v.toFixed(4)}</Table.Td><Table.Td c="dimmed">{t(BAND_NAME[k] ?? "")}</Table.Td></Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </details>
              <Text size="10px" c="dimmed">{pt.lat.toFixed(5)}, {pt.lon.toFixed(5)} · {t("snimaka u periodu")}: {pt.snimaka_u_periodu}, {t("bez oblaka")}: {pt.bez_oblaka}</Text>
            </Stack>
          )}
          </div>
        </DockCard>
  ) : null;

  return { controls, legend, result };
}

function shiftDate(d: string, days: number) {
  const x = new Date(d + "T12:00:00Z");
  x.setUTCDate(x.getUTCDate() + days);
  return x.toISOString().slice(0, 10);
}

function cls(idx: Index, v: number) {
  return idx.classes.find((c, i) => v < c.do || i === idx.classes.length - 1)?.naziv ?? null;
}

/** Skala indeksa sa vrijednostima i klasama (šta znači koja boja). */
function IndexLegend({ idx }: { idx: Index }) {
  const { t } = useT();
  const lo = idx.ramp[0][0], hi = idx.ramp[idx.ramp.length - 1][0];
  const colorAt = (v: number) => {
    const p = (v - lo) / (hi - lo || 1);
    let best = idx.ramp[0][1];
    for (const [val, c] of idx.ramp) if ((val - lo) / (hi - lo || 1) <= p + 1e-9) best = c;
    return best;
  };
  const mid = (lo + hi) / 2;
  return (
    <Stack gap={4}>
      <div style={{ height: 10, borderRadius: 3, background: `linear-gradient(90deg, ${idx.ramp.map(([, c]) => c).join(",")})` }} />
      <Group justify="space-between"><Text size="10px" c="dimmed">{lo}</Text><Text size="10px" c="dimmed">{mid.toFixed(2)}</Text><Text size="10px" c="dimmed">{hi}</Text></Group>
      {idx.classes.map((c) => (
        <Group key={c.naziv} gap={6} wrap="nowrap" align="flex-start">
          <span style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 1, background: colorAt((c.od + c.do) / 2), border: "1px solid rgba(0,0,0,.2)" }} />
          <Text size="xs" lh={1.25}><Text span ff="monospace" size="xs">{c.od.toFixed(2)}–{c.do.toFixed(2)}</Text> {t(c.naziv)}</Text>
        </Group>
      ))}
      <Text size="10px" c="dimmed">{t("Providno = oblak, sjenka ili snijeg (maskirano).")}</Text>
    </Stack>
  );
}
